import { Controller } from "@hotwired/stimulus";

import { DESKTOP_MIN_WIDTH, prefersReducedMotion } from "~/lib/viewport";
import { whileInView } from "~/lib/web/loop";

/**
 * Everything that makes the preview a preview rather than a player: no
 * controls, no keyboard, no related videos, and muted, which is what browsers
 * require before they will start a video the reader did not ask for.
 *
 * Muting turns captions on: YouTube takes a muted video as one whose sound
 * cannot be heard, and shows its auto-generated track over the loop. No embed
 * parameter turns that off. cc_load_policy=1 forces captions on and 0 is the
 * documented default, which leaves the auto track showing, so a video used here
 * needs its automatic captions disabled on the video itself.
 */
const PREVIEW_PARAMS =
  "autoplay=1&mute=1&controls=0&loop=1&playsinline=1&rel=0" +
  "&disablekb=1&fs=0&iv_load_policy=3&modestbranding=1";

/**
 * A muted loop of the walkthrough while it is on screen, which becomes the
 * real player with sound when the reader clicks it.
 *
 * The lite-youtube facade stays in the markup underneath and is what the click
 * activates, so the page without this controller, and every reader the gate
 * below excludes, still gets the poster and a player on demand. The preview
 * costs a YouTube embed on scroll, so it is desktop only, and skipped for a
 * reader who has asked for reduced motion or is saving data.
 */
export default class WebVideoController extends Controller<HTMLElement> {
  private release: (() => void) | null = null;
  private listeners: AbortController | null = null;
  private preview: HTMLIFrameElement | null = null;
  private isInView = false;

  override connect(): void {
    if (!this.isPreviewable()) return;
    this.release = whileInView(
      this.element,
      () => {
        this.isInView = true;
        this.show();
      },
      () => {
        this.isInView = false;
        this.hide();
      },
      0.4,
    );
    // The loop does not survive a hidden tab: the video reaches its end and
    // stays there, and an ended or paused player shows YouTube's title, channel
    // and More videos panel over the section, none of which controls=0
    // suppresses. A frame discarded on the way out autoplays from the start on
    // the way back, and costs a background tab nothing in the meantime.
    this.listeners = new AbortController();
    document.addEventListener(
      "visibilitychange",
      () => {
        if (document.hidden) this.hide();
        else if (this.isInView) this.show();
      },
      { signal: this.listeners.signal },
    );
  }

  override disconnect(): void {
    this.release?.();
    this.release = null;
    this.listeners?.abort();
    this.listeners = null;
    this.hide();
  }

  private isPreviewable(): boolean {
    if (window.innerWidth < DESKTOP_MIN_WIDTH) return false;
    if (prefersReducedMotion()) return false;
    const connection = (navigator as { connection?: { saveData?: boolean } })
      .connection;
    return connection?.saveData !== true;
  }

  private show(): void {
    if (this.preview !== null) return;
    const id = this.facade()?.getAttribute("videoid");
    if (!id) return;

    const preview = document.createElement("iframe");
    // Over the facade, which stays underneath as the click target: the click
    // has to reach this controller, not YouTube's own iframe. An iframe is a
    // replaced element, so inset-0 alone leaves it at its intrinsic 300x150.
    preview.className =
      "pointer-events-none absolute inset-0 h-full w-full border-0 rounded-2xl md:rounded-3xl";
    preview.title = "";
    preview.setAttribute("aria-hidden", "true");
    preview.setAttribute("tabindex", "-1");
    preview.allow = "autoplay; encrypted-media";
    // The site sends Referrer-Policy: same-origin, so without this the frame
    // reaches YouTube with no Referer and is answered with error 153 instead
    // of the player. lite-youtube sets the same attribute for the same reason.
    preview.referrerPolicy = "strict-origin-when-cross-origin";
    // playlist repeats the one video, which is what loop needs to loop a
    // single id rather than a real playlist.
    preview.src =
      `https://www.youtube-nocookie.com/embed/${id}` +
      `?${PREVIEW_PARAMS}&playlist=${id}`;
    this.element.appendChild(preview);
    this.preview = preview;
  }

  private hide(): void {
    this.preview?.remove();
    this.preview = null;
  }

  /** Hands the click to the facade, which loads the player with its sound. */
  play(): void {
    this.hide();
    this.release?.();
    this.release = null;
    this.facade()?.click();
  }

  private facade(): HTMLElement | null {
    return this.element.querySelector("lite-youtube");
  }
}
